/**
 * @module components/MixedAxisConfig
 * @description 组合图 Y 轴页签 — 主/次轴（metric 字符串 + 图表类型）
 */

import React, { useCallback } from 'react';
import { useIntl } from 'umi';
import { useQuerySelector } from '../../hooks/deps';
import DualAxisPair from '../common/DualAxis/DualAxisPair';
import AxisNameField from '../common/DualAxis/fields/AxisNameField';
import AxisMetricSingleField from '../common/DualAxis/fields/AxisMetricSingleField';
import AxisChartTypeField from '../common/DualAxis/fields/AxisChartTypeField';
import AxisRangeField from '../common/DualAxis/fields/AxisRangeField';
import { useMixedAxisDraft } from './useMixedAxisDraft';
import { useAxisMetricOptions } from '../common/DualAxis/hooks/useAxisMetricOptions';
import type { IMixedAxisConfig, TAxisKey } from '../common/DualAxis/types';
import { useMixedMetricLinkage } from './useMixedMetricLinkage';
import '../common/DualAxis/index.scss';
import './index.scss';

interface IMixedAxisConfigProps {
  visibleItems?: Record<string, boolean>;
}

const MixedAxisConfig: React.FC<IMixedAxisConfigProps> = () => {
  const { formatMessage } = useIntl();
  const rawColumnMetrics = useQuerySelector((s) => s.columnMetrics) || [];
  const columnMetrics = Array.isArray(rawColumnMetrics)
    ? (rawColumnMetrics as Record<string, unknown>[])
    : [];

  const { primaryAxis, secondaryAxis, dispatchAxis, dispatchAxes } =
    useMixedAxisDraft(columnMetrics);

  const { metricValues, selectOptions, getMetricByUid } =
    useAxisMetricOptions(columnMetrics);

  const handleMetricChange = useMixedMetricLinkage({
    primaryAxis,
    secondaryAxis,
    metricValues,
    getMetricByUid,
    dispatchAxes,
  });

  const handleAxisChartTypeChange = useCallback(
    (axisKey: TAxisKey, newType: string) => {
      const otherKey: TAxisKey =
        axisKey === 'primaryYAxis' ? 'secondYAxis' : 'primaryYAxis';
      const currentAxis =
        axisKey === 'primaryYAxis' ? primaryAxis : secondaryAxis;
      const otherAxis =
        axisKey === 'primaryYAxis' ? secondaryAxis : primaryAxis;
      const otherType = newType === 'chart-bar' ? 'chart-line' : 'chart-bar';
      dispatchAxes({
        [axisKey]: { ...currentAxis, chartType: newType },
        [otherKey]: { ...otherAxis, chartType: otherType },
      });
    },
    [dispatchAxes, primaryAxis, secondaryAxis],
  );

  const buildSection = (
    axisKey: TAxisKey,
    axis: IMixedAxisConfig,
    axisChartType: string,
    onChartTypeChange: (value: string) => void,
  ) => (
    <>
      <AxisNameField
        value={axis.yAxisName || ''}
        onChange={(v) => dispatchAxis(axisKey, { yAxisName: v })}
      />
      <AxisMetricSingleField
        value={axis.metric || ''}
        options={selectOptions}
        onChange={(metric) => handleMetricChange(axisKey, metric)}
      />
      <AxisChartTypeField
        value={axisChartType}
        onChange={onChartTypeChange}
      />
      <AxisRangeField
        value={!!axis.yAxisRangeSwitch}
        onChange={(v) => dispatchAxis(axisKey, { yAxisRangeSwitch: v })}
      />
    </>
  );

  const sections = [
    {
      key: 'primaryYAxis',
      title: formatMessage({ id: 'dbl_chart_primary_axis' }),
      content: buildSection(
        'primaryYAxis',
        primaryAxis,
        primaryAxis.chartType || 'chart-bar',
        (v) => handleAxisChartTypeChange('primaryYAxis', v),
      ),
    },
    {
      key: 'secondYAxis',
      title: formatMessage({ id: 'dbl_chart_secondary_axis' }),
      content: buildSection(
        'secondYAxis',
        secondaryAxis,
        secondaryAxis.chartType || 'chart-line',
        (v) => handleAxisChartTypeChange('secondYAxis', v),
      ),
    },
  ];

  return (
    <div className="dbl-mixed-axis-config">
      <DualAxisPair sections={sections} />
    </div>
  );
};

export default React.memo(MixedAxisConfig);
